import React from 'react';
import {
  Button,
  CloudUploadIcon,
  ListItemIcon,
  MenuItem,
  useConfirmationModal,
} from '@cherre-frontend/ui';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { useTheme } from '@material-ui/core';
import { useCherreEventWithRecoil } from '@cherre-frontend/data-fetching';
import { useHistory } from 'react-router';
import { useFetchAndSetCustomPropertyAttributes } from '../../../hooks/useCustomPropertyAttributes';

type UploadPropertyButtonProps = {
  confirmFunction: typeof useConfirmationModal.arguments.confirm;
  mode: 'upload' | 'bulk'; // upload => first upload, bulk => following uploads
  variant?: 'button' | 'menu-item';
};

export const dialogTitleUpload = 'Upload Properties';
export const dialogTitleBulk = 'Bulk Upload Properties';

export const buttonLabelUpload = 'Upload Property';
export const buttonLabelBulk = 'Bulk Upload';

export const UploadPropertyButton: React.FC<UploadPropertyButtonProps> = ({
  confirmFunction,
  mode,
  variant = 'button',
}) => {
  const theme = useTheme();
  const { push } = useHistory();

  const isAdminUploadPropertyEnabled = useFeatureFlag('DspAdminUploadProperty');
  const newIngest = useFeatureFlag('DSPNewIngestionAdmin');

  const customAttributesValue = useFetchAndSetCustomPropertyAttributes()[0];

  const customAttributes = React.useMemo(
    () =>
      customAttributesValue
        ?.filter((attribute) => attribute.active !== false)
        .map((attribute) => ({
          name: attribute.slug,
          label: attribute.label,
          dataType: attribute.type,
          mandatory: attribute.required,
        })),
    [customAttributesValue]
  );
  const target_schema = React.useMemo(
    () => [
      {
        name: 'entity_id',
        label: 'Source Entity ID',
        dataType: 'string',
        mandatory: false,
      },
      {
        name: 'property_code',
        label: 'Target Entity ID',
        dataType: 'string',
        mandatory: true,
      },
      {
        name: 'entity_name',
        label: 'Source Entity Name',
        dataType: 'string',
        mandatory: false,
      },
      {
        name: 'property_name',
        label: 'Target Entity Name',
        dataType: 'string',
        mandatory: false,
      },
      {
        name: 'address',
        label: 'Address',
        dataType: 'string',
        mandatory: false,
      },
      {
        name: 'fund',
        label: 'Fund',
        dataType: 'string',
        mandatory: false,
      },
      {
        name: 'type',
        label: 'Type',
        dataType: 'string',
        mandatory: false,
      },
    ],
    []
  );

  const mergedAttributes = {
    data: [...(target_schema || []), ...(customAttributes || [])],
  };

  const onUploadClick = useCherreEventWithRecoil(
    'use requested upload property',
    () => async () => {
      let title: string;
      let primaryText: string;

      if (mode === 'upload') {
        title = dialogTitleUpload;
        primaryText = `Upload your master property list (including both 1:1 and many-to-one property mappings) as a XLS or CSV file, including the source-to-target entity mapping from your service providers.`;
      } else {
        title = dialogTitleBulk;
        primaryText = `Bulk upload new properties to your existing master property list (including both 1:1 and many-to-one property mappings) as a XLS or CSV file, including the source-to-target entity mapping from your service providers.`;
      }

      const fieldList = mergedAttributes.data?.map((field, index) => (
        <li key={field.label ? String(field.label) : index}>
          {field.label}
          {field.mandatory && (
            <span style={{ color: theme.palette.error.main }}>&nbsp;*</span>
          )}
        </li>
      ));

      confirmFunction({
        title,
        content: (
          <div style={{ fontFamily: theme.typography.fontFamily }}>
            <p>{primaryText}</p>
            <p>Please include the fields in your upload:</p>
            <div
              style={{
                maxHeight: '200px',
                overflowY: 'auto', // Enable vertical scrolling
              }}
            >
              <ul>{fieldList}</ul>
            </div>
            <p style={{ color: theme.palette.grey[700] }}>
              Fields marked with asterisks (*) are mandatory.
            </p>
          </div>
        ),
        confirmLabel: 'Continue',
        handleConfirm: () => {
          push(`/dsp/upload-property${newIngest ? '-new' : ''}`);
        },
      });
    }
  );

  if (!isAdminUploadPropertyEnabled) {
    return null;
  }

  const buttonLabel = mode === 'upload' ? buttonLabelUpload : buttonLabelBulk;

  if (variant === 'button') {
    return (
      <Button
        startIcon={<CloudUploadIcon />}
        onClick={() => onUploadClick()}
        variant='contained'
        color='primary'
        sx={{ marginTop: '12px' }}
      >
        <span>{buttonLabel}</span>
      </Button>
    );
  }

  return (
    <MenuItem onClick={() => onUploadClick()}>
      <ListItemIcon>
        <CloudUploadIcon />
      </ListItemIcon>
      <span>{buttonLabel}</span>
    </MenuItem>
  );
};
